import { useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, Package, Barcode, Tag, DollarSign, Layers, Edit, Printer, Maximize2,
  Building, Scale, Ruler, Palette, FileText, Info, Hash, Clock, Shield,
} from 'lucide-react';
import { useProduct } from '../../hooks/useProducts';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { toast } from 'sonner';
import api from '../../api/clients';

const COLOR_NAMES: Record<string, string> = {
  '#000000': 'Negro', '#FFFFFF': 'Blanco', '#FF0000': 'Rojo', '#0000FF': 'Azul',
  '#008000': 'Verde', '#FFFF00': 'Amarillo', '#FFA500': 'Naranja', '#800080': 'Morado',
  '#FFC0CB': 'Rosa', '#808080': 'Gris', '#A52A2A': 'Marrón', '#00FFFF': 'Cian',
  '#FF00FF': 'Magenta', '#C0C0C0': 'Plateado', '#FFD700': 'Dorado', '#FF6347': 'Tomate',
  '#6A5ACD': 'Pizarra', '#20B2AA': 'Aguamarina', '#FF69B4': 'Rosa fuerte', '#8B4513': 'Marrón oscuro',
};

const getColorName = (hex: string | null): string => {
  if (!hex) return 'Sin color';
  const normalized = hex.toUpperCase();
  return COLOR_NAMES[normalized] || hex;
};

export function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const returnPage = searchParams.get('returnPage') || '1';

  const { data: product, isLoading, isError } = useProduct(id ? parseInt(id) : 0);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const handleDownloadPDF = async () => {
    if (!product?.documentoPdfUrl) {
      toast.info('Generando PDF...');
      try {
        const response = await api.post(`/products/${id}/pdf`);
        if (response.data.success) {
          window.open(response.data.pdfUrl, '_blank');
          toast.success('PDF generado');
        }
      } catch (error) {
        toast.error('Error al generar el PDF');
      }
      return;
    }
    window.open(product.documentoPdfUrl, '_blank');
  };

  const openPreview = () => {
    if (product?.imagenUrl) {
      setPreviewImage(product.imagenUrl);
    }
  };

  const closePreview = () => {
    setPreviewImage(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando producto...</div>
      </div>
    );
  }

  if (isError || !product) {
    return (
      <div className="text-center py-12 text-red-500">
        Error al cargar el producto
      </div>
    );
  }

  const isLowStock = product.stock <= product.stockMinimo && product.stockMinimo > 0;
  const isOutOfStock = product.stock === 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/productos?page=${returnPage}`)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold text-gray-800">{product.nombre}</h1>
          <Badge variant={product.activo ? 'success' : 'danger'}>
            {product.activo ? 'Activo' : 'Inactivo'}
          </Badge>
          {isOutOfStock && <Badge variant="danger">Agotado</Badge>}
          {isLowStock && !isOutOfStock && <Badge variant="warning">Stock Bajo</Badge>}
          {product.reserva && <Badge variant="warning">Reserva</Badge>}
        </div>
        <div className="flex gap-2">
          {product.documentoPdfUrl && (
            <Button onClick={handleDownloadPDF} variant="outline" className="flex items-center gap-2">
              <Printer size={16} />
              Ver PDF
            </Button>
          )}
          <Button
            onClick={() => navigate(`/productos/editar/${product.id}?returnPage=${returnPage}`)} // 👈 Pasa la página a Editar
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Edit size={16} />
            Editar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <Card className="p-4">
            <div className="aspect-square bg-gray-50 rounded-lg overflow-hidden flex items-center justify-center relative group">
              {product.imagenUrl ? (
                <>
                  <img
                    src={product.imagenUrl}
                    alt={product.nombre}
                    className="w-full h-full object-contain p-4 cursor-pointer"
                    onClick={openPreview}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/placeholder-image.png';
                    }}
                  />
                  <button
                    onClick={openPreview}
                    className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                    title="Ver imagen ampliada"
                  >
                    <Maximize2 size={24} className="text-white" />
                  </button>
                </>
              ) : (
                <div className="text-center text-gray-400">
                  <Package size={48} className="mx-auto mb-2" />
                  <p>Sin imagen</p>
                </div>
              )}
            </div>
            {product.imagenUrl && (
              <p className="text-xs text-gray-400 mt-2 text-center">
                Haz clic en la imagen para ampliarla
              </p>
            )}
          </Card>
        </div>

        <div className="lg:col-span-3 space-y-6">
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Info size={20} className="text-blue-500" />
              Información General
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <Tag size={14} /> Nombre
                </p>
                <p className="font-medium">{product.nombre}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <Barcode size={14} /> Código de Barras
                </p>
                <p className="font-medium">{product.codigoBarras || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <Hash size={14} /> SKU
                </p>
                <p className="font-medium">{product.sku || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <Hash size={14} /> Código Fabricante
                </p>
                <p className="font-medium">{product.codigoFabricante || '-'}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <FileText size={14} /> Descripción
                </p>
                <p className="font-medium">{product.descripcion || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <Layers size={14} /> Categoría
                </p>
                <p className="font-medium">{product.categoriaRel?.nombre || product.categoria || 'Sin categoría'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <Building size={14} /> Proveedor
                </p>
                <p className="font-medium">{product.proveedor?.nombre || 'No asignado'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <FileText size={14} /> Marca
                </p>
                <p className="font-medium">{product.marca || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <FileText size={14} /> Modelo
                </p>
                <p className="font-medium">{product.modelo || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <Palette size={14} /> Color
                </p>
                <div className="flex items-center gap-3 mt-1">
                  <div
                    className="w-6 h-6 rounded-full border-2 border-gray-300 shrink-0"
                    style={{ backgroundColor: product.color || '#000000' }}
                    title={product.color || 'Sin color'}
                  />
                  <span className="font-medium">
                    {getColorName(product.color)}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <Shield size={14} /> Garantía
                </p>
                <p className="font-medium">{product.garantia || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <Scale size={14} /> Peso
                </p>
                <p className="font-medium">{product.peso ? `${product.peso} kg` : '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <Ruler size={14} /> Dimensiones
                </p>
                <p className="font-medium">{product.dimensiones || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <Package size={14} /> Precio de Costo
                </p>
                <p className="font-medium">{formatCurrency(product.precioCosto || 0)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <DollarSign size={14} /> Precio Contado
                </p>
                <p className="font-medium">{formatCurrency(product.precioContado || 0)}</p>
              </div>
              {product.observaciones && (
                <div className="md:col-span-2">
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <FileText size={14} /> Observaciones
                  </p>
                  <p className="font-medium">{product.observaciones}</p>
                </div>
              )}
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <DollarSign size={20} className="text-orange-500" />
              Datos de Costo
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-500">Ganancia</p>
                <p className="font-medium text-green-600">{product.ganancia || 0}%</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Ingreso Bruto</p>
                <p className="font-medium text-yellow-600">{product.ingresoBruto || 0}%</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Costo Adicional</p>
                <p className="font-medium">{formatCurrency(product.costoAdicional || 0)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Comisiones</p>
                <p className="font-medium">{formatCurrency(product.comisiones || 0)}</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex justify-between items-center">
                <p className="text-sm font-medium text-gray-500">Precio Final</p>
                <p className="text-2xl font-bold text-blue-600">{formatCurrency(product.precioFinal || product.precio || 0)}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Package size={20} className="text-green-500" />
              Precios y Stock
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Precio</p>
                <p className="font-medium text-lg">{formatCurrency(product.precio || 0)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Stock</p>
                <p className={`font-medium text-lg ${
                  isOutOfStock ? 'text-red-600' : 
                  isLowStock ? 'text-yellow-600' : 
                  'text-green-600'
                }`}>
                  {product.stock || 0} unidades
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Stock Mínimo</p>
                <p className="font-medium">{product.stockMinimo || 0}</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-2">
              <Badge variant={product.activo ? 'success' : 'danger'}>
                {product.activo ? ' Activo' : ' Inactivo'}
              </Badge>
              {product.documentoPdfUrl && (
                <Badge variant="info">PDF disponible</Badge>
              )}
              {isOutOfStock && <Badge variant="danger">Agotado</Badge>}
              {isLowStock && !isOutOfStock && <Badge variant="warning">Stock Bajo</Badge>}
              {product.reserva && <Badge variant="warning">Reserva</Badge>}
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Clock size={20} className="text-gray-500" />
              Información del Sistema
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Creado</p>
                <p className="font-medium">{formatDate(product.createdAt)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Última actualización</p>
                <p className="font-medium">{formatDate(product.updatedAt)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">ID del Producto</p>
                <p className="font-medium text-gray-400">#{product.id}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Estado</p>
                <p className="font-medium">
                  {product.status === 'available' && <span className="text-green-600">Disponible</span>}
                  {product.status === 'low_stock' && <span className="text-yellow-600">Stock Bajo</span>}
                  {product.status === 'out_of_stock' && <span className="text-red-600">Agotado</span>}
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <Modal
        isOpen={!!previewImage}
        onClose={closePreview}
        title={product.nombre}
        size="lg"
      >
        <div className="flex items-center justify-center min-h-75">
          {previewImage && (
            <img
              src={previewImage}
              alt={product.nombre}
              className="max-w-full max-h-[70vh] object-contain rounded-lg"
              onError={(e) => {
                (e.target as HTMLImageElement).src = '/placeholder-image.png';
              }}
            />
          )}
        </div>
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-500">{product.nombre}</p>
          <button
            onClick={() => {
              if (previewImage) {
                window.open(previewImage, '_blank');
              }
            }}
            className="mt-2 text-sm text-blue-600 hover:underline"
          >
            Abrir imagen en nueva pestaña
          </button>
        </div>
      </Modal>
    </div>
  );
}